<script setup>
const props = defineProps({
  activity: {
    type: Object,
    default: () => {},
  },
});

const ITEM_VERB_MAP = {
  CREATED: 'created the item',
  UPDATED: 'updated the item',
  DEACTIVATED: 'deactivated item',
};

const STOCK_VERB_MAP = {
  STOCK_IN: activity => `marked ${activity.meta?.stock?.count} ${activity.meta?.item?.uom} as ${activity.meta?.status?.to?.name} for the item ${activity.meta?.item?.name}`,
  STOCK_OUT: activity => `marked ${activity.meta?.stock?.count} ${activity.meta?.item?.uom} as ${activity.meta?.status?.to?.name} for the item ${activity.meta?.item?.name}`,
  MOVE: activity => `moved ${activity.meta?.stock?.count} ${activity.meta?.item?.uom} of ${activity.meta?.item?.name} from ${activity.meta?.location?.from?.name} to ${activity.meta?.location?.to?.name}`,
};

const ADJUSTMENT_VERB_MAP = {
  CREATED: 'created the transaction',
  UPDATED: 'updated the transaction',
  PUBLISHED: 'published the transaction',
  NOTIFICATION_SENT: (activity) => {
    return `sent ${activity.meta?.adjustment?.type} notification to ${activity.meta?.notification?.to}`;
  },
  ASSOCIATED: (activity) => {
    return `associated the transaction ${activity.meta?.associated_adjustment?.type}`;
  },
  DISASSOCIATED: (activity) => {
    return `dissociated the transaction ${activity.meta?.associated_adjustment?.type}`;
  },
  MARKED_AS_FULFILLED: 'marked the transaction as fully reconciled',
  RESET_STATUS: 'reset the reconciliation status',
  FIELD_VALUES_UPDATED: 'updated metadata for the adjustment',
  MEMBERS_UPDATED: 'updated the members for the transaction',
};

const PURCHASE_ORDER_VERB_MAP = {
  CREATED: 'created the purchase order',
  UPDATED: 'updated the purchase order',
  PUBLISHED: 'published the purchase order',
  NOTIFICATION_SENT: (activity) => {
    return `sent ${activity.meta?.adjustment?.type} notification to ${activity.meta?.notification?.to}`;
  },
  ASSOCIATED: (activity) => {
    return `associated the purchase order ${activity.meta?.associated_adjustment?.type}`;
  },
  DISASSOCIATED: (activity) => {
    return `dissociated the purchase order ${activity.meta?.associated_adjustment?.type}`;
  },
  FIELD_VALUES_UPDATED: 'updated metadata for the adjustment',
  MEMBERS_UPDATED: 'updated the members for the purchase order',
};

const WAREHOUSE_VERB_MAP = {
  CREATED: 'created the warehouse',
  UPDATED: 'updated the warehouse',
  DEACTIVATED: 'deleted the warehouse',
};

const VENDOR_VERB_MAP = {
  CREATED: 'created the vendor',
  UPDATED: 'updated the vendor',
  DEACTIVATED: 'deleted the vendor',
};

const CUSTOM_FIELD_VERB_MAP = {
  CREATED: 'created the field',
  UPDATED: 'updated the field',
  DELETED: 'deleted the field',
};

function getVerbMap(resource) {
  switch (resource) {
    case 'ITEM':
      return ITEM_VERB_MAP;
    case 'STOCK':
      return STOCK_VERB_MAP;
    case 'ADJUSTMENT':
      return ADJUSTMENT_VERB_MAP;
    case 'PURCHASE_ORDER':
      return PURCHASE_ORDER_VERB_MAP;
    case 'WAREHOUSE':
      return WAREHOUSE_VERB_MAP;
    case 'VENDOR':
      return VENDOR_VERB_MAP;
    case 'CUSTOM_FIELD':
      return CUSTOM_FIELD_VERB_MAP;
    default:
      return {};
  }
}

function getActivityText(activity) {
  const verb_map = getVerbMap(activity.resource_name);

  if (typeof verb_map[activity.verb] === 'function')
    return verb_map[activity.verb](activity);
  return verb_map[activity.verb];
}
</script>

<template>
  <div class="flex flex-wrap">
    <div v-if="Object.keys(getVerbMap(props.activity.resource_name)).includes(props.activity.verb)" class="text-xs">
      {{ getActivityText(activity) }}
    </div>
    <div v-else-if="props.activity.verb === 'NOTIFICATION_SENT'" class="text-xs flex items-start">
      <p class="whitespace-nowrap mr-2">
        {{ props.activity.meta.notification.type }} {{ props.activity.meta.notification.channel }} notification sent to
      </p>
      <div>
        <HawkBadge v-for="email in props.activity.meta.notification.to" :key="email" class="mb-2">
          {{ email }}
        </HawkBadge>
      </div>
    </div>
  </div>
</template>
